class SchoolSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.resultsContainer = this.querySelector('.school-search__results');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);

    this.states = {
      OPEN: 'school-search-open',
      LOADING: 'btn--loading',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get sectionId() {
    return 'school-search-results';
  }

  onFocus(event) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.fetchResults(url);
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.hideResults();
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    const search_term = this.getQuery();
    url.searchParams.set('q', search_term);
    url.searchParams.set('resources[type]', 'collection');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', this.sectionId);
    return url;
  }

  onChange() {
    const query = this.getQuery();
    if (query.length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.fetchResults(url);
  }

  fetchResults(url) {
    // Check cache first
    if (this.cachedMap.has(url)) {
      this.renderResults(this.cachedMap.get(url));
      return;
    }

    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching school search data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }

  renderResults(responseText) {
    if (!this.resultsContainer) return;

    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(responseText, 'text/html');
    const sectionElement = parsedDoc.querySelector(`#shopify-section-${this.sectionId}`);

    if (sectionElement) {
      this.resultsContainer.innerHTML = sectionElement.innerHTML;
      this.showResults();
    } else {
      // Try to find content directly in the response
      const hasResults = parsedDoc.querySelector('.school-search-results');
      if (hasResults) {
        this.resultsContainer.innerHTML = hasResults.outerHTML;
        this.showResults();
      } else {
        // If no results, check if it's an empty state
        const emptyState = parsedDoc.querySelector('.empty-state');
        if (emptyState) {
          this.resultsContainer.innerHTML = emptyState.outerHTML;
          this.showResults();
        } else {
          this.hideResults();
        }
      }
    }
  }

  showResults() {
    if (this.searchContent) {
      this.searchContent.classList.remove('hidden');
      this.classList.add(this.states.OPEN);
      this.setAttribute('results', '');
      // Ensure aria-expanded is set
      if (this.input) {
        this.input.setAttribute('aria-expanded', 'true');
      }
    }
  }

  hideResults() {
    if (this.searchContent) {
      this.searchContent.classList.add('hidden');
      this.classList.remove(this.states.OPEN);
      this.removeAttribute('results');
      // Ensure aria-expanded is set
      if (this.input) {
        this.input.setAttribute('aria-expanded', 'false');
      }
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    if (!this.contains(target)) {
      this.hideResults();
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      if (this.getQuery().length > 0) {
        this.setAttribute('results', '');
      }
    }
  }
}

customElements.define('school-search', SchoolSearch);

